import { useState, useEffect, useCallback, useRef, memo } from 'react';
import { apiFetch } from '../../services/api';
import { toast } from '../../hooks/useToast';
import { getToken } from '../../services/api';
import { NotesSkeleton } from '../Skeleton';
import {
  Plus, Search, Pin, PinOff, Trash2, Save,
  FileText, Link2, Tag, X, Copy, ImageIcon,
  Bold, Italic, Code, Heading1, Heading2, Heading3,
  List, ListChecks, Quote, Minus, Link, Eye, Edit3,
  SortAsc, ChevronDown, Upload, Wallet, CheckSquare, Download, GripVertical,
  FolderIcon, FolderPlus, Pencil, ChevronRight,
} from 'lucide-react';

interface Note {
  id: number;
  title: string;
  content_md: string;
  is_pinned: number;
  pin_order: number;
  tag_ids: string | null;
  folder_id: number | null;
  created_at: string;
  updated_at: string;
}

interface NoteFolder {
  id: number;
  name: string;
  color: string;
  icon: string;
  sort_order: number;
}

interface TagItem {
  id: number;
  name: string;
  color: string;
}

interface NoteLink {
  source_id: number;
  target_id: number;
}

type SortMode = 'updated' | 'created' | 'name';

// ─── Enhanced markdown renderer ────────────────────────────
function highlightCode(code: string, lang: string): string {
  const normalizedLang = lang.toLowerCase();
  const kw: Record<string, string[]> = {
    javascript: ['const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while', 'class', 'import', 'export', 'from', 'async', 'await', 'new', 'typeof', 'instanceof', 'true', 'false', 'null', 'undefined', 'this', 'switch', 'case', 'break', 'continue', 'throw', 'try', 'catch', 'finally', 'delete', 'in', 'of'],
    typescript: ['const', 'let', 'var', 'function', 'return', 'if', 'else', 'for', 'while', 'class', 'import', 'export', 'from', 'async', 'await', 'new', 'typeof', 'instanceof', 'true', 'false', 'null', 'undefined', 'this', 'interface', 'type', 'enum', 'extends', 'implements', 'switch', 'case', 'break', 'continue', 'throw', 'try', 'catch', 'finally', 'as', 'keyof', 'readonly'],
    python: ['def', 'return', 'if', 'else', 'elif', 'for', 'while', 'class', 'import', 'from', 'as', 'async', 'await', 'True', 'False', 'None', 'in', 'not', 'and', 'or', 'pass', 'break', 'continue', 'raise', 'try', 'except', 'finally', 'with', 'lambda', 'yield', 'global', 'nonlocal'],
    sql: ['SELECT', 'FROM', 'WHERE', 'INSERT', 'INTO', 'VALUES', 'UPDATE', 'DELETE', 'CREATE', 'TABLE', 'DROP', 'ALTER', 'ADD', 'COLUMN', 'INDEX', 'AND', 'OR', 'NOT', 'IN', 'IS', 'NULL', 'ORDER', 'BY', 'GROUP', 'HAVING', 'JOIN', 'LEFT', 'RIGHT', 'INNER', 'OUTER', 'ON', 'AS', 'LIMIT', 'OFFSET', 'DISTINCT', 'COUNT', 'SUM', 'MAX', 'MIN', 'AVG'],
    bash: ['if', 'then', 'else', 'fi', 'for', 'while', 'do', 'done', 'echo', 'export', 'local', 'return', 'function', 'case', 'esac', 'in'],
  };
  const jsAlias: Record<string, string> = { js: 'javascript', ts: 'typescript', py: 'python', sh: 'bash', bash: 'bash' };
  const resolvedLang = jsAlias[normalizedLang] || normalizedLang;
  const keywords = kw[resolvedLang] || [];
  
  // Code is already HTML-escaped at this point (& < > are escaped)
  let result = code;
  
  // Single-line comments (do before strings to avoid matching inside strings)
  if (['javascript', 'typescript', 'java', 'c', 'cpp', 'csharp'].includes(resolvedLang)) {
    result = result.replace(/(\/\/[^\n]*)/g, '<span class="syn-comment">$1</span>');
    result = result.replace(/(\/\*[\s\S]*?\*\/)/g, '<span class="syn-comment">$1</span>');
  } else if (resolvedLang === 'python') {
    result = result.replace(/(#[^\n]*)/g, '<span class="syn-comment">$1</span>');
  } else if (resolvedLang === 'sql') {
    result = result.replace(/(--[^\n]*)/g, '<span class="syn-comment">$1</span>');
  } else if (resolvedLang === 'bash') {
    result = result.replace(/(#[^\n]*)/g, '<span class="syn-comment">$1</span>');
  }
  
  // Strings (double and single quotes, template literals already use `)
  result = result.replace(/("(?:[^"\\]|\\.)*")/g, '<span class="syn-string">$1</span>');
  result = result.replace(/('(?:[^'\\]|\\.)*')/g, '<span class="syn-string">$1</span>');
  
  // Numbers
  result = result.replace(/\b(\d+\.?\d*)\b(?![^<]*>)/g, '<span class="syn-number">$1</span>');
  
  // Keywords (only if not inside already-replaced spans)
  if (keywords.length > 0) {
    const kwPattern = keywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    result = result.replace(new RegExp(`(?<![\\w<])\\b(${kwPattern})\\b(?![\\w])`, 'g'), '<span class="syn-keyword">$1</span>');
  }
  
  return result;
}

function processNestedLists(text: string): string {
  const lines = text.split('\n');
  const out: string[] = [];
  
  interface LItem { indent: number; content: string; ordered: boolean }
  const listLines: LItem[] = [];
  
  const flushList = () => {
    if (listLines.length === 0) return;
    const minIndent = listLines.reduce((m, l) => Math.min(m, l.indent), Infinity);
    
    function buildHtml(items: LItem[], base: number): string {
      const isOrdered = items.find(i => i.indent === base)?.ordered ?? false;
      const tag = isOrdered ? 'ol' : 'ul';
      let html = '';
      let i = 0;
      while (i < items.length) {
        if (items[i].indent !== base) { i++; continue; }
        const children: LItem[] = [];
        let j = i + 1;
        while (j < items.length && items[j].indent > base) { children.push(items[j]); j++; }
        const childBase = children.length > 0 ? children.reduce((m, c) => Math.min(m, c.indent), Infinity) : base + 2;
        html += `<li>${items[i].content}${children.length > 0 ? buildHtml(children, childBase) : ''}</li>`;
        i = j;
      }
      return `<${tag}>${html}</${tag}>`;
    }
    
    out.push(buildHtml(listLines, minIndent));
    listLines.length = 0;
  };
  
  for (const line of lines) {
    const ul = line.match(/^(\s*)[-*] (.+)$/);
    const ol = line.match(/^(\s*)(\d+)\. (.+)$/);
    if (ul) {
      listLines.push({ indent: ul[1].length, content: ul[2], ordered: false });
    } else if (ol) {
      listLines.push({ indent: ol[1].length, content: ol[3], ordered: true });
    } else {
      flushList();
      out.push(line);
    }
  }
  flushList();
  return out.join('\n');
}

function renderMarkdown(md: string): string {
  let html = md
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Code blocks (must be first to prevent inner processing)
  html = html.replace(/```(\w*)\n([\s\S]*?)```/g, (_m, lang, code) => {
    const highlighted = lang ? highlightCode(code.trim(), lang) : code.trim();
    return `<pre class="code-block"><code class="language-${lang}">${highlighted}</code></pre>`;
  });

  // Tables
  html = html.replace(/^(\|.+\|)\n(\|[-:\| ]+\|)\n((?:\|.+\|\n?)*)/gm, (_m, header, _sep, body) => {
    const headers = header.split('|').filter((c: string) => c.trim()).map((c: string) => `<th>${c.trim()}</th>`).join('');
    const rows = body.trim().split('\n').map((row: string) => {
      const cells = row.split('|').filter((c: string) => c.trim()).map((c: string) => `<td>${c.trim()}</td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('');
    return `<table><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
  });

  html = html
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>');

  // Checkboxes
  html = html
    .replace(/^- \[x\] (.+)$/gm, '<li class="task-done"><input type="checkbox" checked disabled> $1</li>')
    .replace(/^- \[ \] (.+)$/gm, '<li class="task-todo"><input type="checkbox" disabled> $1</li>');

  // Strikethrough
  html = html.replace(/~~(.+?)~~/g, '<del>$1</del>');

  html = html
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');

  html = html.replace(/`(.+?)`/g, '<code>$1</code>');

  // Images
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="rounded-lg max-w-full">');

  html = html.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Note references: [[Note Title]]
  html = html.replace(/\[\[(.+?)\]\]/g, '<span class="note-ref" data-note-ref="$1">📎 $1</span>');

  html = html.replace(/^&gt; (.+)$/gm, '<blockquote>$1</blockquote>');

  html = html.replace(/^---$/gm, '<hr>');

  // Nested lists (handles - item, 1. item, with indentation support)
  html = processNestedLists(html);

  html = html.replace(/\n{2,}/g, '</p><p>');
  html = html.replace(/\n/g, '<br>');

  // Wrap orphan <li> groups (from task checkboxes) in <ul>
  html = html.replace(/(<li[^>]*>.*?<\/li>\s*)+/g, '<ul>$&</ul>');

  return `<p>${html}</p>`;
}

// ─── Image upload helper ────────────────────────────────────
async function uploadImageFile(file: File): Promise<string | null> {
  const ALLOWED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml'];
  if (!ALLOWED.includes(file.type)) {
    toast('Недопустимый формат изображения', 'error');
    return null;
  }
  if (file.size > 10 * 1024 * 1024) {
    toast('Файл слишком большой (макс. 10 МБ)', 'error');
    return null;
  }
  const formData = new FormData();
  formData.append('file', file);
  try {
    const token = getToken();
    const headers: HeadersInit = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch('/api/upload/image', { method: 'POST', headers, body: formData });
    const data = await res.json();
    if (data.success) {
      // Use proxy URL instead of presigned URL (presigned URLs expire after 1 hour)
      return `/api/upload/image/${data.data.key}`;
    }
    toast(data.error || 'Ошибка загрузки', 'error');
    return null;
  } catch {
    toast('Ошибка загрузки изображения', 'error');
    return null;
  }
}

// ─── Toolbar ────────────────────────────────────────────────
interface ToolbarProps {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  onInput: (value: string) => void;
}

function MarkdownToolbar({ textareaRef, onInput }: ToolbarProps) {
  const insert = (before: string, after = '') => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = ta.value.substring(start, end);
    const replacement = before + (sel || 'текст') + after;
    const newValue = ta.value.substring(0, start) + replacement + ta.value.substring(end);
    onInput(newValue);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length, start + before.length + (sel || 'текст').length);
    }, 0);
  };

  const insertLine = (prefix: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const before = ta.value.substring(0, start);
    const needNewline = before.length > 0 && !before.endsWith('\n') ? '\n' : '';
    const newValue = before + needNewline + prefix + ta.value.substring(start);
    onInput(newValue);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + needNewline.length + prefix.length, start + needNewline.length + prefix.length);
    }, 0);
  };

  const buttons = [
    { icon: Bold, action: () => insert('**', '**'), title: 'Жирный (Ctrl+B)' },
    { icon: Italic, action: () => insert('*', '*'), title: 'Курсив (Ctrl+I)' },
    { icon: Code, action: () => insert('`', '`'), title: 'Код' },
    null,
    { icon: Heading1, action: () => insertLine('# '), title: 'Заголовок 1' },
    { icon: Heading2, action: () => insertLine('## '), title: 'Заголовок 2' },
    { icon: Heading3, action: () => insertLine('### '), title: 'Заголовок 3' },
    null,
    { icon: List, action: () => insertLine('- '), title: 'Список' },
    { icon: ListChecks, action: () => insertLine('- [ ] '), title: 'Чекбокс' },
    { icon: Quote, action: () => insertLine('> '), title: 'Цитата' },
    { icon: Minus, action: () => insertLine('---\n'), title: 'Разделитель' },
    null,
    { icon: Link, action: () => insert('[', '](url)'), title: 'Ссылка (Ctrl+K)' },
    { icon: Link2, action: () => insert('[[', ']]'), title: 'Ссылка на заметку [[Название]]' },
    { icon: ImageIcon, action: () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        toast('Загрузка изображения...');
        const url = await uploadImageFile(file);
        if (url) insertLine(`![изображение](${url})\n`);
      };
      input.click();
    }, title: 'Загрузить изображение' },
  ];

  return (
    <div className="flex items-center gap-0.5 px-3 py-1.5 border-b border-slate-700 bg-slate-800/30">
      {buttons.map((btn, i) =>
        btn === null ? (
          <div key={i} className="w-px h-4 bg-slate-700 mx-1" />
        ) : (
          <button
            key={i}
            onClick={btn.action}
            title={btn.title}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
          >
            <btn.icon size={14} />
          </button>
        )
      )}
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────
interface NotesViewProps {
  initialNoteId?: number | null;
  onNoteOpened?: () => void;
  pendingAction?: 'create' | 'save' | null;
  onActionHandled?: () => void;
}

export function NotesView({ initialNoteId, onNoteOpened, pendingAction, onActionHandled }: NotesViewProps) {
  const [notes, setNotes] = useState<Note[]>([]);
  const [tags, setTags] = useState<TagItem[]>([]);
  const [links, setLinks] = useState<NoteLink[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editTags, setEditTags] = useState<number[]>([]);
  const [editLinks, setEditLinks] = useState<number[]>([]);
  const [showGraph, setShowGraph] = useState(false);
  const [showTagInput, setShowTagInput] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [filterTag, setFilterTag] = useState<number | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>('updated');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dragPinnedRef = useRef<number | null>(null);
  const [dragOverPinned, setDragOverPinned] = useState<number | null>(null);
  const [linkedBudget, setLinkedBudget] = useState<{ id: number; amount: number; type: string; description: string; date: string; category_name: string | null; category_color: string | null; card_name: string | null }[]>([]);
  const [linkedTasks, setLinkedTasks] = useState<{ id: number; title: string; status: string; priority: number; deadline: string | null }[]>([]);
  const [loaded, setLoaded] = useState(false);
  // Folders
  const [folders, setFolders] = useState<NoteFolder[]>([]);
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [editingFolderId, setEditingFolderId] = useState<number | null>(null);
  const [folderName, setFolderName] = useState('');
  const [folderColor, setFolderColor] = useState('#6366f1');
  const [folderIcon, setFolderIcon] = useState('📁');
  const [dragNoteOverFolder, setDragNoteOverFolder] = useState<number | null>(null);

  useEffect(() => {
    return () => { if (saveTimeout.current) clearTimeout(saveTimeout.current); };
  }, []);

  const load = useCallback(async () => {
    const [nRes, tRes, lRes, fRes] = await Promise.all([
      apiFetch('/api/notes'),
      apiFetch('/api/notes/tags'),
      apiFetch('/api/notes/links'),
      apiFetch('/api/notes/folders'),
    ]);
    const nData = await nRes.json();
    const tData = await tRes.json();
    const lData = await lRes.json();
    const fData = await fRes.json();
    if (nData.success) setNotes(nData.data);
    if (tData.success) setTags(tData.data);
    if (lData.success) setLinks(lData.data);
    if (fData.success) setFolders(fData.data);
    setLoaded(true);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('app:refresh', handler);
    return () => window.removeEventListener('app:refresh', handler);
  }, [load]);

  // Handle hotkey actions from App
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!pendingAction) return;
    if (pendingAction === 'create') createNote();
    else if (pendingAction === 'save' && editing && selected) saveNote();
    onActionHandled?.();
  }, [pendingAction]);

  // Auto-select note when navigated from Tasks
  useEffect(() => {
    if (initialNoteId && notes.length > 0) {
      const note = notes.find(n => n.id === initialNoteId);
      if (note) { selectNote(note); onNoteOpened?.(); }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialNoteId, notes]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!editing) return;
      const ta = textareaRef.current;
      if (!ta) return;
      if (e.ctrlKey && e.key === 'b') {
        e.preventDefault();
        const s = ta.selectionStart, end = ta.selectionEnd;
        const sel = ta.value.substring(s, end);
        setEditContent(ta.value.substring(0, s) + '**' + (sel || 'текст') + '**' + ta.value.substring(end));
      }
      if (e.ctrlKey && e.key === 'i') {
        e.preventDefault();
        const s = ta.selectionStart, end = ta.selectionEnd;
        const sel = ta.value.substring(s, end);
        setEditContent(ta.value.substring(0, s) + '*' + (sel || 'текст') + '*' + ta.value.substring(end));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editing]);

  const selectedNote = notes.find((n) => n.id === selected);

  const selectNote = (note: Note) => {
    setSelected(note.id);
    setEditTitle(note.title);
    setEditContent(note.content_md);
    const tagIds = note.tag_ids ? note.tag_ids.split(',').map(Number).filter(Boolean) : [];
    setEditTags(tagIds);
    const noteLinks = links.filter((l) => l.source_id === note.id).map((l) => l.target_id);
    setEditLinks(noteLinks);
    setEditing(false);
    // Load linked budget entries
    apiFetch(`/api/budget/entries/by-note/${note.id}`).then(r => r.json()).then(d => {
      if (d.success) setLinkedBudget(d.data);
      else setLinkedBudget([]);
    }).catch(() => setLinkedBudget([]));
    // Load linked tasks
    apiFetch(`/api/tasks/by-note/${note.id}`).then(r => r.json()).then(d => {
      if (d.success) setLinkedTasks(d.data);
      else setLinkedTasks([]);
    }).catch(() => setLinkedTasks([]));
  };

  const createNote = async () => {
    const res = await apiFetch('/api/notes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: 'Новая заметка', content_md: '' }),
    });
    const data = await res.json();
    if (data.success) {
      await load();
      setSelected(data.data.id);
      setEditTitle('Новая заметка');
      setEditContent('');
      setEditTags([]);
      setEditLinks([]);
      setEditing(true);
      toast('Заметка создана');
    }
  };

  const duplicateNote = async () => {
    if (!selectedNote) return;
    const res = await apiFetch('/api/notes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: `Копия — ${selectedNote.title}`, content_md: selectedNote.content_md }),
    });
    const data = await res.json();
    if (data.success) {
      await load();
      setSelected(data.data.id);
      setEditing(false);
      toast('Заметка скопирована');
    }
  };

  const exportNoteMarkdown = () => {
    if (!selectedNote) return;
    const content = `# ${selectedNote.title}\n\n${selectedNote.content_md}`;
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeName = selectedNote.title.replace(/[^a-zA-Z0-9а-яА-ЯёЁ\s_-]/g, '').trim() || 'note';
    a.href = url; a.download = `${safeName}.md`;
    a.click(); URL.revokeObjectURL(url);
  };

  const saveNote = async () => {
    if (!selected) return;
    await apiFetch(`/api/notes/${selected}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: editTitle,
        content_md: editContent,
        tag_ids: editTags,
        linked_note_ids: editLinks,
      }),
    });
    await load();
    toast('Сохранено');
  };

  const autoSave = useCallback(() => {
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(saveNote, 1500);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, editTitle, editContent, editTags, editLinks]);

  const deleteNote = async (id: number) => {
    if (!confirm('Удалить заметку?')) return;
    await apiFetch(`/api/notes/${id}`, { method: 'DELETE' });
    if (selected === id) { setSelected(null); setEditing(false); }
    await load();
    toast('Заметка удалена');
  };

  const togglePin = async (note: Note) => {
    await apiFetch(`/api/notes/${note.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_pinned: note.is_pinned ? 0 : 1 }),
    });
    await load();
  };

  const reorderPinned = useCallback(async (dragId: number, overId: number) => {
    if (dragId === overId) return;
    const pinned = notes.filter(n => n.is_pinned).sort((a, b) => (a.pin_order ?? 0) - (b.pin_order ?? 0));
    const others = notes.filter(n => !n.is_pinned);
    const dragIdx = pinned.findIndex(n => n.id === dragId);
    const overIdx = pinned.findIndex(n => n.id === overId);
    if (dragIdx === -1 || overIdx === -1) return;
    const reordered = [...pinned];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(overIdx, 0, moved);
    const updated = reordered.map((n, i) => ({ ...n, pin_order: i }));
    setNotes([...updated, ...others]);
    await apiFetch('/api/notes/reorder-pinned', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: updated.map(n => ({ id: n.id, pin_order: n.pin_order })) }),
    });
  }, [notes]);

  // Folder CRUD
  const saveFolder = async () => {
    if (!folderName.trim()) return;
    const body = { name: folderName.trim(), color: folderColor, icon: folderIcon };
    if (editingFolderId) {
      await apiFetch(`/api/notes/folders/${editingFolderId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      toast('Папка обновлена');
    } else {
      await apiFetch('/api/notes/folders', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      toast('Папка создана');
    }
    setShowFolderForm(false); setEditingFolderId(null); setFolderName(''); setFolderColor('#6366f1'); setFolderIcon('📁');
    await load();
  };
  const deleteFolder = async (id: number) => {
    if (!confirm('Удалить папку? Заметки останутся без папки.')) return;
    await apiFetch(`/api/notes/folders/${id}`, { method: 'DELETE' });
    await load();
    toast('Папка удалена');
  };
  const moveNoteToFolder = async (noteId: number, folderId: number | null) => {
    await apiFetch(`/api/notes/${noteId}/move`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ folder_id: folderId }) });
    await load();
  };

  const createTag = async () => {
    if (!newTagName.trim()) return;
    await apiFetch('/api/notes/tags', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: newTagName.trim() }),
    });
    setNewTagName('');
    setShowTagInput(false);
    await load();
    toast('Тег создан');
  };

  const toggleTag = (tagId: number) => {
    setEditTags((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId]
    );
  };

  const toggleLink = (noteId: number) => {
    setEditLinks((prev) =>
      prev.includes(noteId) ? prev.filter((n) => n !== noteId) : [...prev, noteId]
    );
  };

  const filtered = notes
    .filter((n) => {
      if (filterTag !== null) {
        const noteTags = n.tag_ids ? n.tag_ids.split(',').map(Number) : [];
        if (!noteTags.includes(filterTag)) return false;
      }
      if (!search) return true;
      const q = search.toLowerCase();
      return n.title.toLowerCase().includes(q) || n.content_md.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (a.is_pinned !== b.is_pinned) return b.is_pinned - a.is_pinned;
      if (a.is_pinned && b.is_pinned) return (a.pin_order ?? 0) - (b.pin_order ?? 0);
      switch (sortMode) {
        case 'name': return a.title.localeCompare(b.title, 'ru');
        case 'created': return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        default: return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      }
    });

  const pinnedFiltered = filtered.filter(n => n.is_pinned);
  const unpinnedFiltered = filtered.filter(n => !n.is_pinned);

  const wordCount = editContent.trim() ? editContent.trim().split(/\s+/).length : 0;

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) return;
    for (const file of files) {
      toast('Загрузка изображения...');
      const url = await uploadImageFile(file);
      if (url) {
        setEditContent(prev => prev + `\n![изображение](${url})\n`);
        autoSave();
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  if (!loaded) return <NotesSkeleton />;

  return (
    <div className="flex h-full">
      {/* Note list */}
      <div className="w-72 border-r border-slate-700 flex flex-col shrink-0">
        <div className="p-3 border-b border-slate-700 space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Поиск..."
                className="w-full bg-slate-800 border border-slate-600 rounded-xl pl-8 pr-8 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                  <X size={12} />
                </button>
              )}
            </div>
            <button onClick={createNote} className="p-1.5 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-white transition-colors" title="Новая заметка">
              <Plus size={16} />
            </button>
          </div>

          <div className="flex items-center gap-1 flex-wrap">
            <button
              onClick={() => setShowGraph(!showGraph)}
              className={`text-xs px-2 py-1 rounded-lg transition-colors ${showGraph ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white bg-slate-800'}`}
            >
              <Link2 size={12} className="inline mr-1" />Граф
            </button>

            <div className="relative ml-auto">
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className="text-xs px-2 py-1 rounded-lg text-slate-400 hover:text-white bg-slate-800 flex items-center gap-1"
              >
                <SortAsc size={12} />
                <ChevronDown size={10} />
              </button>
              {showSortMenu && (
                <div className="absolute right-0 top-full mt-1 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-20 py-1 min-w-[120px]">
                  {[
                    { id: 'updated' as SortMode, label: 'По изменению' },
                    { id: 'created' as SortMode, label: 'По созданию' },
                    { id: 'name' as SortMode, label: 'По имени' },
                  ].map((s) => (
                    <button
                      key={s.id}
                      onClick={() => { setSortMode(s.id); setShowSortMenu(false); }}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-700 transition-colors ${sortMode === s.id ? 'text-indigo-400' : 'text-slate-300'}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              <button
                onClick={() => setFilterTag(null)}
                className={`text-[10px] px-1.5 py-0.5 rounded-full transition-colors ${filterTag === null ? 'bg-indigo-600/30 text-indigo-300 border border-indigo-500/30' : 'text-slate-500 hover:text-slate-300 bg-slate-800/50 border border-transparent'}`}
              >
                Все
              </button>
              {tags.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setFilterTag(filterTag === t.id ? null : t.id)}
                  className={`text-[10px] px-1.5 py-0.5 rounded-full transition-colors border ${filterTag === t.id ? 'bg-indigo-600/30 text-indigo-300 border-indigo-500/30' : 'text-slate-500 hover:text-slate-300 bg-slate-800/50 border-transparent'}`}
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full mr-0.5" style={{ background: t.color }} />
                  {t.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Folder form trigger */}
        <div className="px-3 py-1 border-b border-slate-700 flex items-center justify-between">
          <span className="text-[10px] text-slate-600 uppercase tracking-widest">Папки</span>
          <button onClick={() => { setShowFolderForm(true); setEditingFolderId(null); setFolderName(''); setFolderColor('#6366f1'); setFolderIcon('📁'); }}
            className="text-slate-600 hover:text-indigo-400 transition-colors" title="Новая папка">
            <FolderPlus size={12} />
          </button>
        </div>

        {showFolderForm && (
          <div className="px-3 py-2 border-b border-slate-700">
            <div className="bg-slate-800/50 rounded-lg p-2 space-y-2">
              <input value={folderName} onChange={(e) => setFolderName(e.target.value)} placeholder="Имя папки"
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500/50" autoFocus
                onKeyDown={(e) => e.key === 'Enter' && saveFolder()} />
              <div className="flex gap-1 flex-wrap">
                {['📁', '📂', '🗂️', '📋', '📝', '💡', '⭐', '🔖', '📌', '🏠', '💼', '🎯', '🔬', '🎨', '📊', '🔧'].map(ic => (
                  <button key={ic} onClick={() => setFolderIcon(ic)}
                    className={`w-6 h-6 flex items-center justify-center rounded text-sm ${folderIcon === ic ? 'ring-1 ring-indigo-500 bg-indigo-500/20' : 'bg-slate-800 hover:bg-slate-700'}`}>{ic}</button>
                ))}
              </div>
              <div className="flex gap-1">
                {['#6366f1', '#ef4444', '#22c55e', '#f59e0b', '#ec4899', '#06b6d4', '#8b5cf6', '#f97316'].map(c => (
                  <button key={c} onClick={() => setFolderColor(c)}
                    className={`w-5 h-5 rounded-full ${folderColor === c ? 'ring-2 ring-white scale-110' : ''}`} style={{ background: c }} />
                ))}
              </div>
              <div className="flex gap-1">
                <button onClick={saveFolder} className="flex-1 py-1 bg-indigo-600 hover:bg-indigo-500 text-white text-[10px] rounded-lg transition-colors">
                  {editingFolderId ? 'Сохранить' : 'Создать'}
                </button>
                <button onClick={() => setShowFolderForm(false)} className="px-2 py-1 bg-slate-700 text-slate-300 text-[10px] rounded-lg transition-colors">
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="px-3 py-1.5 text-[10px] text-slate-600 border-b border-slate-800">
          {filtered.length} {filtered.length === 1 ? 'заметка' : filtered.length < 5 ? 'заметки' : 'заметок'}
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Pinned notes */}
          {pinnedFiltered.length > 0 && (
            <>
              <div className="px-3 py-1 text-[9px] text-slate-600 uppercase tracking-widest border-b border-slate-800">Закреплённые</div>
              {pinnedFiltered.map((note) => {
                const noteTags = note.tag_ids ? note.tag_ids.split(',').map(Number) : [];
                const noteTagItems = tags.filter((t) => noteTags.includes(t.id));
                const isDragOver = dragOverPinned === note.id;
                return (
                  <div
                    key={note.id}
                    draggable
                    onDragStart={(e) => { dragPinnedRef.current = note.id; e.dataTransfer.setData('text/note-id', String(note.id)); }}
                    onDragOver={(e) => { e.preventDefault(); setDragOverPinned(note.id); }}
                    onDrop={(e) => { e.preventDefault(); if (dragPinnedRef.current !== null) reorderPinned(dragPinnedRef.current, note.id); dragPinnedRef.current = null; setDragOverPinned(null); }}
                    onDragEnd={() => { dragPinnedRef.current = null; setDragOverPinned(null); }}
                    onClick={() => selectNote(note)}
                    className={`px-3 py-2.5 cursor-pointer border-b border-slate-800/50 transition-all group ${
                      isDragOver ? 'bg-indigo-600/20 border-l-2 border-l-indigo-400' :
                      selected === note.id
                        ? 'bg-indigo-600/10 border-l-2 border-l-indigo-500'
                        : 'hover:bg-slate-800/30 border-l-2 border-l-transparent'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-1.5 flex-1 min-w-0">
                        <GripVertical size={13} className="text-slate-700 shrink-0 mt-0.5 cursor-grab active:cursor-grabbing" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <Pin size={11} className="text-indigo-400 shrink-0" />
                            <span className="text-sm font-medium truncate text-white">{note.title || 'Без названия'}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{note.content_md.slice(0, 60) || '—'}</p>
                          {noteTagItems.length > 0 && (
                            <div className="flex gap-1 mt-1.5">
                              {noteTagItems.slice(0, 3).map((t) => (
                                <span key={t.id} className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-800/80 text-slate-400 flex items-center gap-0.5">
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
                                  {t.name}
                                </span>
                              ))}
                              {noteTagItems.length > 3 && <span className="text-[9px] text-slate-600">+{noteTagItems.length - 3}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2">
                        <button onClick={(e) => { e.stopPropagation(); togglePin(note); }} className="text-slate-500 hover:text-indigo-400">
                          <PinOff size={13} />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); deleteNote(note.id); }} className="text-slate-500 hover:text-red-400">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {/* Folder sections — collapsible */}
          {folders.sort((a, b) => a.sort_order - b.sort_order).map(f => {
            const folderNotes = unpinnedFiltered.filter(n => n.folder_id === f.id);
            const isExpanded = expandedFolders.has(f.id);
            if (folderNotes.length === 0 && filterTag !== null) return null;
            return (
              <div key={`folder-${f.id}`}>
                {/* Folder header row — same height as a note */}
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragNoteOverFolder(f.id); }}
                  onDragLeave={() => setDragNoteOverFolder(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    const noteId = e.dataTransfer.getData('text/note-id');
                    if (noteId) { moveNoteToFolder(parseInt(noteId), f.id); }
                    setDragNoteOverFolder(null);
                  }}
                  className={`px-3 py-2.5 cursor-pointer border-b border-slate-800/50 transition-all group ${
                    dragNoteOverFolder === f.id ? 'bg-indigo-600/20 ring-1 ring-indigo-500/50' : 'hover:bg-slate-800/30'
                  }`}
                  style={{ borderLeft: `3px solid ${f.color}` }}
                >
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setExpandedFolders(prev => {
                        const next = new Set(prev);
                        if (next.has(f.id)) next.delete(f.id); else next.add(f.id);
                        return next;
                      })}
                      className="flex items-center gap-2 flex-1 min-w-0 text-left"
                    >
                      <ChevronRight size={14} className={`text-slate-500 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      <span className="text-base shrink-0">{f.icon || '📁'}</span>
                      <span className="text-sm font-medium truncate text-white">{f.name}</span>
                      <span className="text-[10px] text-slate-600 ml-1 shrink-0">{folderNotes.length}</span>
                    </button>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2">
                      <button onClick={(e) => { e.stopPropagation(); setEditingFolderId(f.id); setFolderName(f.name); setFolderColor(f.color); setFolderIcon(f.icon || '📁'); setShowFolderForm(true); }}
                        className="text-slate-600 hover:text-white p-0.5"><Pencil size={11} /></button>
                      <button onClick={(e) => { e.stopPropagation(); deleteFolder(f.id); }}
                        className="text-slate-600 hover:text-red-400 p-0.5"><Trash2 size={11} /></button>
                    </div>
                  </div>
                </div>
                {/* Folder contents */}
                {isExpanded && folderNotes.map((note) => {
                  const noteTags = note.tag_ids ? note.tag_ids.split(',').map(Number) : [];
                  const noteTagItems = tags.filter((t) => noteTags.includes(t.id));
                  return (
                    <div
                      key={note.id}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData('text/note-id', String(note.id)); }}
                      onClick={() => selectNote(note)}
                      className={`pl-8 pr-3 py-2.5 cursor-pointer border-b border-slate-800/50 transition-all group ${
                        selected === note.id
                          ? 'bg-indigo-600/10 border-l-2 border-l-indigo-500'
                          : 'hover:bg-slate-800/30 border-l-2 border-l-transparent'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium truncate text-white">{note.title || 'Без названия'}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{note.content_md.slice(0, 60) || '—'}</p>
                          {noteTagItems.length > 0 && (
                            <div className="flex gap-1 mt-1.5">
                              {noteTagItems.slice(0, 3).map((t) => (
                                <span key={t.id} className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-800/80 text-slate-400 flex items-center gap-0.5">
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
                                  {t.name}
                                </span>
                              ))}
                              {noteTagItems.length > 3 && <span className="text-[9px] text-slate-600">+{noteTagItems.length - 3}</span>}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2">
                          <button onClick={(e) => { e.stopPropagation(); moveNoteToFolder(note.id, null); }} className="text-slate-500 hover:text-amber-400" title="Убрать из папки">
                            <FolderIcon size={13} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); togglePin(note); }} className="text-slate-500 hover:text-indigo-400">
                            <Pin size={13} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); deleteNote(note.id); }} className="text-slate-500 hover:text-red-400">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Unfiled notes (no folder) */}
          {(() => {
            const unfiledNotes = unpinnedFiltered.filter(n => n.folder_id === null || n.folder_id === 0);
            if (unfiledNotes.length === 0 && (pinnedFiltered.length > 0 || folders.length > 0)) return null;
            return (
              <>
                {(pinnedFiltered.length > 0 || folders.length > 0) && unfiledNotes.length > 0 && (
                  <div className="px-3 py-1 text-[9px] text-slate-600 uppercase tracking-widest border-b border-slate-800">Без папки</div>
                )}
                {unfiledNotes.map((note) => {
                  const noteTags = note.tag_ids ? note.tag_ids.split(',').map(Number) : [];
                  const noteTagItems = tags.filter((t) => noteTags.includes(t.id));
                  return (
                    <div
                      key={note.id}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData('text/note-id', String(note.id)); }}
                      onClick={() => selectNote(note)}
                      className={`px-3 py-2.5 cursor-pointer border-b border-slate-800/50 transition-all group ${
                        selected === note.id
                          ? 'bg-indigo-600/10 border-l-2 border-l-indigo-500'
                          : 'hover:bg-slate-800/30 border-l-2 border-l-transparent'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-medium truncate text-white">{note.title || 'Без названия'}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 truncate">{note.content_md.slice(0, 60) || '—'}</p>
                          {noteTagItems.length > 0 && (
                            <div className="flex gap-1 mt-1.5">
                              {noteTagItems.slice(0, 3).map((t) => (
                                <span key={t.id} className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-800/80 text-slate-400 flex items-center gap-0.5">
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.color }} />
                                  {t.name}
                                </span>
                              ))}
                              {noteTagItems.length > 3 && <span className="text-[9px] text-slate-600">+{noteTagItems.length - 3}</span>}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2">
                          <button onClick={(e) => { e.stopPropagation(); togglePin(note); }} className="text-slate-500 hover:text-indigo-400">
                            <Pin size={13} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); deleteNote(note.id); }} className="text-slate-500 hover:text-red-400">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}

          {filtered.length === 0 && (
            <div className="text-center text-slate-500 text-sm py-8">
              <FileText size={32} className="mx-auto mb-2 opacity-30" />
              {notes.length === 0 ? 'Нет заметок' : 'Ничего не найдено'}
            </div>
          )}
        </div>
      </div>

      {/* Note content / graph */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {showGraph ? (
          <NoteGraph notes={notes} links={links} onSelect={(id) => { const n = notes.find(x => x.id === id); if (n) selectNote(n); setShowGraph(false); }} />
        ) : selected && selectedNote ? (
          <>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
              {editing ? (
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="flex-1 bg-transparent text-lg font-semibold text-white focus:outline-none"
                  autoFocus
                />
              ) : (
                <div>
                  <h2 className="text-lg font-semibold text-white">{selectedNote.title || 'Без названия'}</h2>
                  <p className="text-[10px] text-slate-600 mt-0.5">Изменено {new Date(selectedNote.updated_at).toLocaleString('ru-RU')}</p>
                </div>
              )}
              <div className="flex gap-2 items-center">
                <button onClick={duplicateNote} className="p-1.5 text-slate-500 hover:text-indigo-400 rounded-lg hover:bg-slate-800 transition-colors" title="Дублировать">
                  <Copy size={14} />
                </button>
                <button onClick={exportNoteMarkdown} className="p-1.5 text-slate-500 hover:text-emerald-400 rounded-lg hover:bg-slate-800 transition-colors" title="Экспорт .md">
                  <Download size={14} />
                </button>
                {editing && (
                  <button onClick={saveNote} className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl transition-colors">
                    <Save size={14} /> Сохранить
                  </button>
                )}
                <button
                  onClick={() => { if (editing) saveNote(); setEditing(!editing); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-sm rounded-xl text-slate-300 transition-colors"
                >
                  {editing ? <><Eye size={14} /> Просмотр</> : <><Edit3 size={14} /> Редактировать</>}
                </button>
              </div>
            </div>

            {editing && (
              <div className="px-4 py-2 border-b border-slate-700 flex flex-wrap items-center gap-1.5">
                <Tag size={14} className="text-slate-500" />
                {tags.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => toggleTag(t.id)}
                    className={`text-xs px-2 py-0.5 rounded-full border transition-all ${
                      editTags.includes(t.id)
                        ? 'border-indigo-500 bg-indigo-500/20 text-indigo-300 shadow-sm shadow-indigo-500/20'
                        : 'border-slate-600 text-slate-400 hover:border-slate-500'
                    }`}
                  >
                    {t.name}
                  </button>
                ))}
                {showTagInput ? (
                  <div className="flex items-center gap-1">
                    <input
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && createTag()}
                      className="bg-slate-800 border border-slate-600 rounded-lg px-2 py-0.5 text-xs w-24 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                      placeholder="Новый тег"
                      autoFocus
                    />
                    <button onClick={createTag} className="text-indigo-400 text-xs">✓</button>
                    <button onClick={() => { setShowTagInput(false); setNewTagName(''); }} className="text-slate-500"><X size={12} /></button>
                  </div>
                ) : (
                  <button onClick={() => setShowTagInput(true)} className="text-xs text-slate-500 hover:text-indigo-400 transition-colors">+ тег</button>
                )}
              </div>
            )}

            {editing && (
              <div className="px-4 py-2 border-b border-slate-700 flex flex-wrap items-center gap-1.5">
                <Link2 size={14} className="text-slate-500" />
                {notes.filter(n => n.id !== selected).map((n) => (
                  <button
                    key={n.id}
                    onClick={() => toggleLink(n.id)}
                    className={`text-xs px-2 py-0.5 rounded-full border transition-all ${
                      editLinks.includes(n.id)
                        ? 'border-green-500 bg-green-500/20 text-green-300 shadow-sm shadow-green-500/20'
                        : 'border-slate-600 text-slate-400 hover:border-slate-500'
                    }`}
                  >
                    {n.title || `#${n.id}`}
                  </button>
                ))}
              </div>
            )}

            {editing && <MarkdownToolbar textareaRef={textareaRef} onInput={setEditContent} />}

            <div className="flex-1 overflow-y-auto p-4">
              {editing ? (
                <textarea
                  ref={textareaRef}
                  value={editContent}
                  onChange={(e) => { setEditContent(e.target.value); autoSave(); }}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  className="w-full h-full bg-transparent text-slate-200 font-mono text-sm resize-none focus:outline-none leading-relaxed"
                  placeholder="Написать заметку в Markdown... (перетащите сюда изображения)"
                />
              ) : (
                <div
                  className="markdown-body prose prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(selectedNote.content_md) }}
                  onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.classList.contains('note-ref')) {
                      const refTitle = target.getAttribute('data-note-ref');
                      if (refTitle) {
                        const refNote = notes.find(n => n.title.toLowerCase() === refTitle.toLowerCase());
                        if (refNote) {
                          selectNote(refNote);
                        } else {
                          toast('Заметка не найдена: ' + refTitle, 'error');
                        }
                      }
                    }
                  }}
                />
              )}

              {/* Linked tasks */}
              {!editing && linkedTasks.length > 0 && (
                <div className="mt-6 pt-4 border-t border-slate-700/50">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckSquare size={14} className="text-violet-400" />
                    <span className="text-xs font-medium text-slate-400">Связанные задачи</span>
                  </div>
                  <div className="space-y-1.5">
                    {linkedTasks.map((t) => {
                      const statusLabels: Record<string, string> = { idea: 'Идея', todo: 'К работе', in_progress: 'В работе', done: 'Готово' };
                      const statusColors: Record<string, string> = { idea: 'text-slate-400', todo: 'text-blue-400', in_progress: 'text-amber-400', done: 'text-green-400' };
                      return (
                        <div key={t.id} className="flex items-center gap-3 bg-slate-800/40 rounded-xl px-3 py-2">
                          <span className={`text-xs font-medium ${statusColors[t.status] ?? 'text-slate-400'}`}>{statusLabels[t.status] ?? t.status}</span>
                          <span className="text-sm text-white truncate flex-1">{t.title}</span>
                          {t.deadline && <span className="text-[10px] text-slate-600">{new Date(t.deadline).toLocaleDateString('ru-RU')}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Linked budget entries */}
              {!editing && linkedBudget.length > 0 && (
                <div className="mt-6 pt-4 border-t border-slate-700/50">
                  <div className="flex items-center gap-2 mb-3">
                    <Wallet size={14} className="text-emerald-400" />
                    <span className="text-xs font-medium text-slate-400">Связанные расходы</span>
                  </div>
                  <div className="space-y-1.5">
                    {linkedBudget.map((e) => (
                      <div key={e.id} className="flex items-center gap-3 bg-slate-800/40 rounded-xl px-3 py-2">
                        <span className={`text-sm font-medium ${e.type === 'income' ? 'text-green-400' : 'text-red-400'}`}>
                          {e.type === 'income' ? '+' : '−'}{e.amount.toLocaleString('ru-RU')} ₽
                        </span>
                        {e.category_name && (
                          <span className="flex items-center gap-1 text-xs">
                            <span className="w-2 h-2 rounded-full" style={{ background: e.category_color || '#6366f1' }} />
                            <span className="text-slate-400">{e.category_name}</span>
                          </span>
                        )}
                        {e.description && <span className="text-xs text-slate-500 truncate">{e.description}</span>}
                        <span className="text-[10px] text-slate-600 ml-auto">{new Date(e.date).toLocaleDateString('ru-RU')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {editing && (
              <div className="px-4 py-1.5 border-t border-slate-700 flex items-center gap-4 text-[10px] text-slate-600">
                <span>{editContent.length} символов</span>
                <span>{wordCount} слов</span>
              </div>
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-500">
            <div className="text-center">
              <FileText size={48} className="mx-auto mb-3 opacity-20" />
              <p className="text-sm">Выберите заметку или создайте новую</p>
              <button onClick={createNote} className="mt-3 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl transition-colors">
                <Plus size={14} className="inline mr-1" /> Новая заметка
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Graph visualization ──────────────────────────────────
interface NoteGraphProps {
  notes: Note[];
  links: NoteLink[];
  onSelect: (id: number) => void;
}

function NoteGraphComponent({ notes, links, onSelect }: NoteGraphProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const positionsRef = useRef<Map<number, { x: number; y: number }>>(new Map());

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.parentElement!.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    const W = canvas.width;
    const H = canvas.height;

    const cx = W / 2;
    const cy = H / 2;
    const radius = Math.min(W, H) * 0.35;
    const positions = new Map<number, { x: number; y: number }>();

    notes.forEach((n, i) => {
      const angle = (2 * Math.PI * i) / notes.length - Math.PI / 2;
      positions.set(n.id, { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) });
    });
    positionsRef.current = positions;

    ctx.clearRect(0, 0, W, H);

    for (const l of links) {
      const s = positions.get(l.source_id);
      const t = positions.get(l.target_id);
      if (s && t) {
        const grad = ctx.createLinearGradient(s.x, s.y, t.x, t.y);
        grad.addColorStop(0, 'rgba(99, 102, 241, 0.5)');
        grad.addColorStop(1, 'rgba(139, 92, 246, 0.3)');
        ctx.strokeStyle = grad;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(t.x, t.y);
        ctx.stroke();
      }
    }

    for (const n of notes) {
      const pos = positions.get(n.id);
      if (!pos) continue;
      const hasLinks = links.some(l => l.source_id === n.id || l.target_id === n.id);
      const nodeRadius = hasLinks ? 10 : 6;

      if (hasLinks) {
        const glow = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, 25);
        glow.addColorStop(0, 'rgba(99, 102, 241, 0.3)');
        glow.addColorStop(1, 'rgba(99, 102, 241, 0)');
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, 25, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.beginPath();
      ctx.arc(pos.x, pos.y, nodeRadius, 0, Math.PI * 2);
      ctx.fillStyle = hasLinks ? '#6366f1' : '#475569';
      ctx.fill();

      ctx.fillStyle = '#e2e8f0';
      ctx.font = '11px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText((n.title || `#${n.id}`).slice(0, 20), pos.x, pos.y + 22);
    }
  }, [notes, links]);

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    for (const [id, pos] of positionsRef.current.entries()) {
      if ((x - pos.x) ** 2 + (y - pos.y) ** 2 < 225) { onSelect(id); return; }
    }
  };

  return (
    <div className="flex-1 relative">
      <canvas ref={canvasRef} className="w-full h-full cursor-pointer" onClick={handleClick} />
      {notes.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center text-slate-500">
          <p>Создайте заметки для визуализации графа</p>
        </div>
      )}
    </div>
  );
}

const NoteGraph = memo(NoteGraphComponent);
